import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useCookies } from "react-cookie";
// helpers
import { applySelectOffer, initMarketplaceSession } from "../utils/analytics/marketplaceSession";
import { pushMarketplaceContext } from "../utils/analytics/pushMarketplaceContext";
import { resolveMarketplaceOfferFromLocation } from "../utils/analytics/resolveMarketplaceOfferFromLocation";
import { PlatformType } from "../utils/helpers/subdomainHelpers/getPlatformType";

/**
 * Fires select-offer marketplace_context when the user opens a distinct transport or GP offer route.
 * - airline (TransferHub): `select_offer_transfer_hub`
 * - hotel (Guest Platform): `select_offer_guest_platform`
 * Captures inbound `source` / `source_detail` on first session init (even before cookie consent).
 * Pushes to the dataLayer only after cookies are accepted.
 */
export function useMarketplaceSelectOfferTracking(): void {
  const location = useLocation();
  const [cookies] = useCookies(["cookieAccepted"]);
  const platformType = useSelector((state: any) => state.global.platformType) as PlatformType;
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);

  useEffect(() => {
    if (!platformType) {
      return;
    }

    // Capture source as early as possible (URL may lose query params after navigation).
    initMarketplaceSession(platformType, location.search);

    if (cookies.cookieAccepted !== true) {
      return;
    }

    const offer = resolveMarketplaceOfferFromLocation(location.pathname, location.state);
    if (!offer) {
      return;
    }

    const { shouldPush, state } = applySelectOffer(offer, platformType);
    if (!shouldPush) {
      return;
    }

    pushMarketplaceContext({
      action: platformType === "hotel" ? "select_offer_guest_platform" : "select_offer_transfer_hub",
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      offerCode: state.current_offer?.offer_code ?? offer.offer_code,
      hotel:
        platformType === "hotel"
          ? {
              id: tenantInfo?.code ?? null,
              name: tenantInfo?.name ?? null
            }
          : undefined
    });
  }, [
    location.pathname,
    location.search,
    location.state,
    cookies.cookieAccepted,
    platformType,
    tenantInfo?.code,
    tenantInfo?.name
  ]);
}
